import { NgModule } from '@angular/core';
import { RouterModule, Routes, UrlSegment } from '@angular/router';
import { CartNavigationComponent } from '@app/account/cart-navigation/cart-navigation.component';
import { InquiryWebFormComponent } from '@app/account/inquiry-web-form/inquiry-web-form.component';
import { LogoutComponent } from '@app/account/logout/logout.component';
import { AddCouponComponent } from '@app/components/add-coupon/add-coupon.component';
import { AddPaymentInfoComponent } from '@app/components/add-payment-info/add-payment-info.component';
import { BirthdayDiscountComponent } from '@app/components/birthday-discount/birthday-discount.component';
import { CommunicationPreferencesComponent } from '@app/components/communication-preferences/communication-preferences.component';
import { ConfirmTravelPartyMembersComponent } from '@app/components/confirm-travel-party-members/confirm-travel-party-members.component';
import { EditPaymentInfoComponent } from '@app/components/edit-payment-info/edit-payment-info.component';
import { EnlargeQrCodeComponent } from '@app/components/enlarge-qr-code/enlarge-qr-code.component';
import { ReservationErrorPageComponent } from '@app/components/htl-link-reservation-error-page/reservation-error-page.component';
import { SignupIdentityVerificationComponent } from '@app/components/identity-verification/signup-identity-verification/signup-identity-verification.component';
import { ConfirmLinkTicketsComponent } from '@app/components/link-ticket/confirm-link-tickets/confirm-link-tickets.component';
import { RegisteredLinkTicketsComponent } from '@app/components/link-ticket/registered-link-tickets/registered-link-tickets.component';
import { RegistrationLinkTicketsComponent } from '@app/components/link-ticket/registration-link-tickets/registration-link-tickets.component';
import { PaymentListComponent } from '@app/components/payment-list/payment-list.component';
import { ShareqrcodeComponent } from '@app/components/share-qr-code/share-qr-code.component';
import { WarningTravelPartyComponent } from '@app/components/warning-travel-party/warning-travel-party.component';
import { BuyTicketsComponent } from '@components/buy-tickets/buy-tickets.component';
import { UpdatePhoneNumberComponent } from '@components/update-phone-number/update-phone-number.component';
import { appRoutes } from '@config/app-default.constant';
import { FEATURE_FLAGS } from '@config/feature-flags.constant';
import { FeatureFlagGuard } from '@upr-web-primer/feature-flag';
import { FlutterLoaderComponent } from '@upr-web-primer/flutter-loader';
import { PageLabelsResolver } from '@upr-web-primer/page-labels';
import { UrlInfoResolver } from '@upr-web-primer/url-info';
import { InquiryConfirmComponent } from '../account/inquiry-confirm/inquiry-confirm.component';
import { LoginComponent } from '../account/login/login.component';
import { LostFoundConfirmComponent } from '../account/lost-found-confirm/lost-found-confirm.component';
import { LostFoundWebFormComponent } from '../account/lost-found-web-form/lost-found-web-form.component';
import { PageNotFoundComponent } from '../account/page-not-found/page-not-found.component';
import { ResetMyPasswordComponent } from '../account/reset-my-password/reset-my-password.component';
import { SigninComponent } from '../account/signin/signin.component';
import { UnsubscribeEmailComponent } from '../account/unsubscribe-email/unsubscribe-email.component';
import { AuthGuard } from '../auth-guard.guard';
import { AddPhoneNumberComponent } from '../components/add-phone-number/add-phone-number.component';
import { ConfirmDeleteComponent } from '../components/confirm-delete/confirm-delete.component';
import { DeleteAccountConfirmationComponent } from '../components/delete-account-confirmation/delete-account-confirmation.component';
import { EditTravelPartyComponent } from '../components/edit-travel-party/edit-travel-party.component';
import { UpdatePasswordComponent } from '../components/update-password/update-password.component';
import { OidcLoginGuard } from '../oidc-login.guard';
import { PageResolver } from '../shared/resolver/page.resolver';
import { SignupIdentityVerificationGuard } from '../signup-identity-verification.guard';
import { TcmPageResolver } from './tcm-page.resolver';

const appPrefix = ':language/:country/';
const indexPage = '/index.html';
export const routes: Routes = [
  {
    matcher: (url: UrlSegment[]): any => {
      // Check if the URL has at least 3 segments and the third segment is 'signup'
      if (url.length > 3 && url[3].path === 'search-results') {
        // URL matches search-results pattern - no-op, fall through to return null
      }
      return null;
    },
    component: PageNotFoundComponent,
  },
  {
    path: 'search-results',
    component: PageNotFoundComponent,
  },
  {
    path: appPrefix + appRoutes.LOGIN,
    component: LoginComponent,
    data: { slug: appRoutes.LOGIN },
    canActivate: [OidcLoginGuard],
    resolve: {
      // urlInfo: UrlInfoResolver,
      // page: TcmPageResolver,
      pageLabels: PageResolver,
    },
  },
  {
    path: appRoutes.LOGOUT,
    component: LogoutComponent,
  },
  {
    path: appRoutes.CART,
    component: CartNavigationComponent,
  },
  {
    path: appRoutes.SIGN_IN,
    component: SigninComponent,
  },
  {
    path: appPrefix + appRoutes.SIGNUP_IDENTITY_VERIFICATION,
    component: SignupIdentityVerificationComponent,
    canActivate: [SignupIdentityVerificationGuard],
    data: { slug: appRoutes.SIGNUP_IDENTITY_VERIFICATION },
    resolve: {
      pageLabels: PageResolver,
    },
  },
  {
    path: appPrefix + appRoutes.CREATE_ACCOUNT + indexPage,
    redirectTo: appPrefix + appRoutes.CREATE_ACCOUNT,
  },
  {
    matcher: (url: UrlSegment[]): any => {
      // Allow create-account routes with or without query parameters. Angular route
      // matchers do not receive query params, so mobile OIDC context such as
      // channel/source/isMobile/ReturnUrl is validated in the component.
      if (url.length === 3 && url[2].path === appRoutes.CREATE_ACCOUNT) {
        return { consumed: url };
      }
      return null;
    },

    loadChildren: () =>
      import('../account/create-account/create-account.module').then(
        (m) => m.CreateAccountModule,
      ),
    data: { slug: appRoutes.CREATE_ACCOUNT },
    resolve: {
      pageLabels: PageResolver,
    },
  },

  {
    path: appPrefix + appRoutes.CREATE_ACCOUNT,
    loadChildren: () =>
      import('../account/create-account/create-account.module').then(
        (m) => m.CreateAccountModule,
      ),
    data: { slug: appRoutes.CREATE_ACCOUNT },
    resolve: {
      pageLabels: PageResolver,
    },
  },

  {
    path: appPrefix + appRoutes.SIGNUP_CONFIRM + indexPage,
    redirectTo: appPrefix + appRoutes.SIGNUP_CONFIRM,
  },
  {
    path: appPrefix + appRoutes.SIGNUP_CONFIRM,
    // component: SignupConfirmComponent,
    loadChildren: () =>
      import('../account/create-account/create-account.module').then(
        (m) => m.CreateAccountModule,
      ),
    data: { slug: appRoutes.SIGNUP_CONFIRM },
    resolve: {
      // urlInfo: UrlInfoResolver,
      // page: TcmPageResolver,
      pageLabels: PageResolver,
    },
  },
  {
    path: appPrefix + appRoutes.SIGNUP_COMPLETE + indexPage,
    redirectTo: appPrefix + appRoutes.SIGNUP_COMPLETE,
  },
  {
    path: appPrefix + appRoutes.SIGNUP_COMPLETE,
    // component: SignupCompleteComponent,
    loadChildren: () =>
      import('../account/create-account/create-account.module').then(
        (m) => m.CreateAccountModule,
      ),
    data: { slug: appRoutes.SIGNUP_COMPLETE },
    resolve: {
      // urlInfo: UrlInfoResolver,
      // page: TcmPageResolver,
      pageLabels: PageResolver,
    },
  },
  {
    path: appPrefix + appRoutes.PACKAGE_CREATE_ACCOUNT + indexPage,
    redirectTo: appPrefix + appRoutes.PACKAGE_CREATE_ACCOUNT,
  },

  {
    path: appPrefix + appRoutes.REGISTER_ACCOUNT,
    loadChildren: () =>
      import('../account/enroll-account-from-order/enroll-account-from-order.module').then(
        (m) => m.EnrollAccountFromOrderModule,
      ),
    data: { slug: appRoutes.REGISTER_ACCOUNT },
    resolve: {
      pageLabels: PageResolver,
    },
  },

  {
    path: appPrefix + appRoutes.EMAIL_SUBSCRIBE + indexPage,
    redirectTo: appPrefix + appRoutes.EMAIL_SUBSCRIBE,
  },

  {
    path: `${appPrefix + appRoutes.EMAIL_SUBSCRIBE}/:interest` + indexPage,
    redirectTo: `${appPrefix + appRoutes.EMAIL_SUBSCRIBE}/:interest`,
  },

  {
    path: appPrefix + appRoutes.EMAIL_UNSUBSCRIBE + indexPage,
    redirectTo: appPrefix + appRoutes.EMAIL_UNSUBSCRIBE,
  },
  {
    path: appPrefix + appRoutes.CONTACT_US + indexPage,
    redirectTo: appPrefix + appRoutes.CONTACT_US,
  },
  {
    path: appPrefix + appRoutes.RESET_PASSWORD + indexPage,
    redirectTo: appPrefix + appRoutes.RESET_PASSWORD,
  },
  {
    path: appPrefix + appRoutes.RESET_PASSWORD,
    component: ResetMyPasswordComponent,
    data: { slug: appRoutes.RESET_PASSWORD },
    resolve: {
      urlInfo: UrlInfoResolver,
      page: TcmPageResolver,
      pageLabels: PageResolver,
    },
  },
  {
    path: appPrefix + appRoutes.DELETE_ACCOUNT + indexPage,
    redirectTo: appPrefix + appRoutes.DELETE_ACCOUNT,
  },
  {
    path: appPrefix + appRoutes.DELETE_ACCOUNT,
    // component: DeleteAccountComponent,
    loadChildren: () =>
      import('../account/delete-account/delete-account.module').then(
        (m) => m.DeleteAccountModule,
      ),
    canActivate: [AuthGuard],
    data: { slug: appRoutes.DELETE_ACCOUNT },

    resolve: {
      // urlInfo: UrlInfoResolver,
      // page: TcmPageResolver,
      pageLabels: PageResolver,
    },
  },
  {
    path: appPrefix + appRoutes.CONFIRM_DELETE + indexPage,
    redirectTo: appPrefix + appRoutes.CONFIRM_DELETE,
  },
  {
    path: appPrefix + appRoutes.CONFIRM_DELETE,
    component: ConfirmDeleteComponent,
    canActivate: [AuthGuard],
    data: { slug: appRoutes.CONFIRM_DELETE },
    resolve: {
      pageLabels: PageResolver,
    },
  },
  {
    path: appPrefix + appRoutes.DELETE_ACCOUNT_CONFIRMATION + indexPage,
    redirectTo: appPrefix + appRoutes.DELETE_ACCOUNT_CONFIRMATION,
  },
  {
    path: appPrefix + appRoutes.DELETE_ACCOUNT_CONFIRMATION,
    component: DeleteAccountConfirmationComponent,
    canActivate: [AuthGuard],
    data: { slug: appRoutes.DELETE_ACCOUNT_CONFIRMATION },
    resolve: {
      // urlInfo: UrlInfoResolver,
      // page: TcmPageResolver,
      pageLabels: PageResolver,
    },
  },
  {
    path: appPrefix + appRoutes.DELETE_ACCOUNT_CONFIRMATION_NA + indexPage,
    redirectTo: appPrefix + appRoutes.DELETE_ACCOUNT_CONFIRMATION,
  },
  {
    path: appPrefix + appRoutes.DELETE_ACCOUNT_CONFIRMATION_NA,
    component: DeleteAccountConfirmationComponent,
    canActivate: [AuthGuard],
    data: { slug: appRoutes.DELETE_ACCOUNT_CONFIRMATION },
    resolve: {
      // urlInfo: UrlInfoResolver,
      // page: TcmPageResolver,
      pageLabels: PageResolver,
    },
  },

  {
    path: appPrefix + appRoutes.MY_PROFILE,
    // component: MyProfileComponent,
    loadChildren: () =>
      import('../account/my-profile/my-profile.module').then(
        (m) => m.MyProfileModule,
      ),
    data: { slug: appRoutes.MY_PROFILE },
    canActivate: [AuthGuard],
  },
  {
    path: appPrefix + appRoutes.UPDATE_PERSONAL_INFO + indexPage,
    redirectTo: appPrefix + appRoutes.UPDATE_PERSONAL_INFO,
  },
  {
    path: appPrefix + appRoutes.UPDATE_PERSONAL_INFO,
    loadChildren: () =>
      import('../account/update-personal-info/update-personal-info.module').then(
        (m) => m.UpdatePersonalInfoModule,
      ),
    data: { slug: appRoutes.UPDATE_PERSONAL_INFO },
    resolve: {
      // urlInfo: UrlInfoResolver,
      // page: TcmPageResolver,
      pageLabels: PageResolver,
    },
  },
  {
    path: appPrefix + appRoutes.UPDATE_PREFERENCES + indexPage,
    redirectTo: appPrefix + appRoutes.UPDATE_PREFERENCES,
  },

  {
    path: appPrefix + appRoutes.UPDATE_PASSWORD + indexPage,
    redirectTo: appPrefix + appRoutes.UPDATE_PASSWORD,
  },
  {
    path: appPrefix + appRoutes.UPDATE_PASSWORD,
    component: UpdatePasswordComponent,
    canActivate: [AuthGuard],
    data: { slug: appRoutes.UPDATE_PASSWORD },
    resolve: {
      // urlInfo: UrlInfoResolver,
      // page: TcmPageResolver,
      pageLabels: PageResolver,
    },
  },
  {
    path: appPrefix + appRoutes.MY_ADDRESSES + indexPage,
    redirectTo: appPrefix + appRoutes.MY_ADDRESSES,
  },
  {
    path: appPrefix + appRoutes.COMM_PREFERENCES,
    component: CommunicationPreferencesComponent,
    resolve: {
      pageLabels: PageResolver,
    },
  },
  {
    path: appPrefix + appRoutes.MY_ADDRESSES,
    loadChildren: () =>
      import('../account/address-list/address-list.module').then(
        (m) => m.AddressListModule,
      ),
    canActivate: [AuthGuard],
    data: { slug: appRoutes.MY_ADDRESSES },
    resolve: {
      // urlInfo: UrlInfoResolver,
      // page: TcmPageResolver,
      pageLabels: PageResolver,
    },
  },
  {
    path: appPrefix + appRoutes.ADD_ADDRESS + indexPage,
    redirectTo: appPrefix + appRoutes.ADD_ADDRESS,
  },
  {
    path: appPrefix + appRoutes.ADD_ADDRESS,
    loadChildren: () =>
      import('../account/add-address/add-address.module').then(
        (m) => m.AddAddressModule,
      ),
    data: { slug: appRoutes.ADD_ADDRESS },
    resolve: {
      // urlInfo: UrlInfoResolver,
      // page: TcmPageResolver,
      pageLabels: PageResolver,
    },
  },
  {
    path: appPrefix + appRoutes.EDIT_ADDRESS + indexPage,
    redirectTo: appPrefix + appRoutes.EDIT_ADDRESS,
  },
  {
    path: appPrefix + appRoutes.EDIT_ADDRESS,
    // component: CreateAccountComponent,
    loadChildren: () =>
      import('../account/update-address/update-address.module').then(
        (m) => m.UpdateAddressModule,
      ),
    data: { slug: appRoutes.ADD_ADDRESS },
    resolve: {
      pageLabels: PageResolver,
    },
  },
  // {
  //   path: appPrefix + appRoutes.EDIT_ADDRESS,
  //   component: UpdateAddressComponent,
  //   data: { slug: appRoutes.ADD_ADDRESS },
  //   resolve: {
  //     // urlInfo: UrlInfoResolver,
  //     // page: TcmPageResolver,
  //     pageLabels: PageResolver,
  //   },
  // },
  {
    path: appPrefix + appRoutes.DASHBOARD + indexPage,
    redirectTo: appPrefix + appRoutes.DASHBOARD,
  },
  {
    path: appPrefix + appRoutes.DASHBOARD,
    // component: DashboardComponent,\
    loadChildren: () =>
      import('../account/dashboard/dashboard.module').then(
        (m) => m.DashboardModule,
      ),
    canActivate: [AuthGuard],
    data: { slug: appRoutes.DASHBOARD },
  },
  {
    path: appPrefix + appRoutes.PHONE_NUMBERS_PAGE,
    loadChildren: () =>
      import('../account/phone-numbers-page/phone-number-page.module').then(
        (m) => m.PhoneNumberModule,
      ),
    canActivate: [AuthGuard],
    data: { slug: appRoutes.PHONE_NUMBERS_PAGE },
    resolve: {
      // urlInfo: UrlInfoResolver,
      // page: TcmPageResolver,
      pageLabels: PageResolver,
    },
  },

  {
    path: appPrefix + appRoutes.UPDATE_PHONE_NUMBER,
    component: UpdatePhoneNumberComponent,
    canActivate: [AuthGuard],
    data: { slug: appRoutes.UPDATE_PHONE_NUMBER },
    resolve: {
      // urlInfo: UrlInfoResolver,
      // page: TcmPageResolver,
      pageLabels: PageResolver,
    },
  },
  {
    path: appPrefix + appRoutes.ADD_PHONE_NUMBER,
    component: AddPhoneNumberComponent,
    canActivate: [AuthGuard],
    data: { slug: appRoutes.UPDATE_PHONE_NUMBER },
    resolve: {
      pageLabels: PageResolver,
    },
  },
  {
    path: appPrefix + appRoutes.TICKET_DETAILS + '/:ticketId',
    loadChildren: () =>
      import('../account/ticket-details/ticket-details.module').then(
        (m) => m.TicketDetailsModule,
      ),
    canActivate: [AuthGuard],
  },
  {
    path: appPrefix + appRoutes.TRAVEL_PARTY_INFORMATION,
    loadChildren: () =>
      import('../account/view-travel-party-information/view-travel-party-information.module').then(
        (m) => m.ViewTravelPartyInformationModule,
      ),
    canActivate: [AuthGuard],
    data: { slug: appRoutes.TRAVEL_PARTY_INFORMATION },
    resolve: {
      pageLabels: PageResolver,
    },
  },
  {
    path: appPrefix + appRoutes.ADD_TRAVEL_PARTY_MEMBERS,
    loadChildren: () =>
      import('../account/add-travel-party/add-travel-party.module').then(
        (m) => m.AddTravelPartyModule,
      ),
    canActivate: [AuthGuard],
    data: { slug: appRoutes.ADD_TRAVEL_PARTY_MEMBERS },
    resolve: {
      // urlInfo: UrlInfoResolver,
      // page: TcmPageResolver,
      pageLabels: PageResolver,
    },
  },
  {
    path: appPrefix + appRoutes.EDIT_TRAVEL_PARTY_MEMBERS,
    component: EditTravelPartyComponent,
    canActivate: [AuthGuard],
    data: { slug: appRoutes.EDIT_TRAVEL_PARTY_MEMBERS },
    resolve: {
      // urlInfo: UrlInfoResolver,
      // page: TcmPageResolver,
      pageLabels: PageResolver,
    },
  },
  {
    path: appPrefix + appRoutes.CONFIRM_TRAVEL_PARTY_MEMBERS,
    component: ConfirmTravelPartyMembersComponent,
    canActivate: [AuthGuard],
    data: { slug: appRoutes.CONFIRM_TRAVEL_PARTY_MEMBERS },
    resolve: {
      // urlInfo: UrlInfoResolver,
      // page: TcmPageResolver,
      pageLabels: PageResolver,
    },
  },
  {
    path: appPrefix + appRoutes.WARNING_TRAVEL_PARTY_MEMBERS,
    component: WarningTravelPartyComponent,
    canActivate: [AuthGuard],
    data: { slug: appRoutes.WARNING_TRAVEL_PARTY_MEMBERS },
    resolve: {
      // urlInfo: UrlInfoResolver,
      // page: TcmPageResolver,
      pageLabels: PageResolver,
    },
  },
  {
    path: appPrefix + appRoutes.PAYMENT_INFO,
    component: PaymentListComponent,
    canActivate: [AuthGuard],
    resolve: {
      pageLabels: PageResolver,
    },
  },
  {
    path: appPrefix + appRoutes.ADD_PAYMENT_INFO,
    component: AddPaymentInfoComponent,
    canActivate: [AuthGuard],
    resolve: {
      pageLabels: PageResolver,
    },
  },
  {
    path: appPrefix + appRoutes.EDIT_PAYMENT_INFO,
    component: EditPaymentInfoComponent,
    canActivate: [AuthGuard],
    resolve: {
      pageLabels: PageResolver,
    },
  },
  {
    path: appPrefix + appRoutes.VIEW_ENTITLMENT + indexPage,
    redirectTo: appPrefix + appRoutes.VIEW_ENTITLMENT,
  },
  {
    path: appPrefix + appRoutes.VIEW_ENTITLMENT,
    loadChildren: () =>
      import('../account/entitlements/entitlement.module').then(
        (m) => m.EntitlementModule,
      ),
    canActivate: [AuthGuard],
    data: { slug: appRoutes.VIEW_ENTITLMENT },
  },
  {
    path: appPrefix + appRoutes.BUY_TICKETS,
    component: BuyTicketsComponent,
    canActivate: [AuthGuard],

    resolve: {
      // urlInfo: UrlInfoResolver,
      // page: TcmPageResolver,
      pageLabels: PageResolver,
    },
  },
  {
    path: appPrefix + appRoutes.ENLARGE_QR_CODE,
    component: EnlargeQrCodeComponent,
    canActivate: [AuthGuard],
    data: { slug: appRoutes.ENLARGE_QR_CODE },
    resolve: {
      // urlInfo: UrlInfoResolver,
      // page: TcmPageResolver,
      pageLabels: PageResolver,
    },
  },
  {
    path: appPrefix + appRoutes.SHARE_QR_CODE + '/:data',
    component: ShareqrcodeComponent,
    data: { slug: appRoutes.SHARE_QR_CODE },
    resolve: {
      // urlInfo: UrlInfoResolver,
      // page: TcmPageResolver,
      pageLabels: PageResolver,
      // entitlementData: DataResolver,
    },
  },
  {
    path: appPrefix + appRoutes.COUPONS,
    // component: CouponsDetailsComponent,
    loadChildren: () =>
      import('../account/coupons/coupons.module').then((m) => m.CouponModule),
    canActivate: [AuthGuard],
  },
  {
    path: appPrefix + appRoutes.LINK_COUPON,
    component: AddCouponComponent,
    canActivate: [AuthGuard],
    resolve: {
      pageLabels: PageResolver,
    },
  },
  {
    path: appPrefix + appRoutes.REGISTER_TICKETS,
    component: RegistrationLinkTicketsComponent,
    canActivate: [AuthGuard],
    resolve: {
      pageLabels: PageResolver,
    },
  },
  {
    path: appPrefix + appRoutes.CONFIRM_TICKETS,
    component: ConfirmLinkTicketsComponent,
    canActivate: [AuthGuard],
    resolve: {
      pageLabels: PageResolver,
    },
  },

  {
    path: appPrefix + appRoutes.CONFIRM_REGISTRATION,
    component: RegisteredLinkTicketsComponent,
    canActivate: [AuthGuard],
    resolve: {
      pageLabels: PageResolver,
    },
  },

  {
    path: appPrefix + appRoutes.ORDER_LIST,
    loadChildren: () =>
      import('../account/order-history/order-history.module').then(
        (m) => m.OrderHistoryModule,
      ),
    canActivate: [AuthGuard],
    resolve: {
      pageLabels: PageResolver,
    },
  },

  {
    path: appPrefix + appRoutes.ORDER_DETAILS + '/:orderId',
    loadChildren: () =>
      import('../account/order-details/order-details.module').then(
        (m) => m.OrderDetailsModule,
      ),
    canActivate: [AuthGuard],
    resolve: {
      pageLabels: PageResolver,
    },
  },
  {
    path: appPrefix + appRoutes.RESERVATION,
    loadChildren: () =>
      import('../account/htl-reservation/htl-reservation.module').then(
        (m) => m.HtlReservationModule,
      ),
    canActivate: [AuthGuard],
    resolve: {
      pageLabels: PageResolver,
    },
  },
  {
    path: appPrefix + appRoutes.RESERVATIONS_ERROR_PAGE + indexPage,
    redirectTo: appPrefix + appRoutes.RESERVATIONS_ERROR_PAGE,
  },
  {
    path: appPrefix + appRoutes.RESERVATIONS_ERROR_PAGE,
    // redirectTo: appPrefix + appRoutes.BIRTHDAY_DISCOUNT,
    component: ReservationErrorPageComponent,
    resolve: {
      pageLabels: PageResolver,
    },
  },
  {
    path: appPrefix + appRoutes.VIEW_VACATION_PURCHASES,
    loadChildren: () =>
      import('../account/vacation-reservation/vacation-reservation.module').then(
        (m) => m.VacationReservationModule,
      ),
    canActivate: [AuthGuard],
    resolve: {
      pageLabels: PageResolver,
    },
  },
  {
    path: appPrefix + appRoutes.RESERVATION_DETAILS + '/:bookingId',
    loadChildren: () =>
      import('../account/htl-reservation-details/htl-reservation-details.module').then(
        (m) => m.HtlReservationDetailModule,
      ),
    canActivate: [AuthGuard],
    resolve: {
      pageLabels: PageResolver,
    },
  },
  {
    path: appPrefix + appRoutes.VACATION_RESERVATION_DETAILS + '/:bookingId',
    loadChildren: () =>
      import('../account/vacation-reservation-details/vacation-reservation-details.module').then(
        (m) => m.ReservationDetailModule,
      ),
    canActivate: [AuthGuard],
    resolve: {
      pageLabels: PageResolver,
    },
  },
  {
    path: appPrefix + appRoutes.LINK_RESERVATIONS,
    loadChildren: () =>
      import('../account/htl-link-reservations/htl-link-reservations.module').then(
        (m) => m.HtlLinkReservationsModule,
      ),
    resolve: {
      pageLabels: PageResolver,
    },
  },
  {
    path: appPrefix + appRoutes.LINK_TICKETS,
    loadChildren: () =>
      import('../account/link-tickets/link-tickets.module').then(
        (m) => m.LinkTicketsModule,
      ),
    canActivate: [AuthGuard],
  },
  {
    path: appPrefix + appRoutes.SAVED_TRIPS,
    loadChildren: () =>
      import('../account/saved-trips/saved-trips.module').then(
        (m) => m.SavedTripsModule,
      ),
    canActivate: [AuthGuard],
  },

  {
    path: appPrefix + appRoutes.DELETE_ITINERARY,
    loadChildren: () =>
      import('../account/delete-itinerary/delete-itinerary.module').then(
        (m) => m.DeleteItineraryModule,
      ),
    canActivate: [AuthGuard],
  },

  {
    path: appPrefix + appRoutes.PAST_TICKETS,
    loadChildren: () =>
      import('../account/past-tickets/past-tickets.module').then(
        (m) => m.PastTicketsModule,
      ),
    canActivate: [AuthGuard],
  },
  {
    path: appPrefix + appRoutes.BIRTHDAY_DISCOUNT,
    // redirectTo: appPrefix + appRoutes.BIRTHDAY_DISCOUNT,
    component: BirthdayDiscountComponent,
    resolve: {
      pageLabels: PageResolver,
    },
  },
  {
    path: appPrefix + appRoutes.UNSUBSCRIBE,
    // redirectTo: appPrefix + appRoutes.BIRTHDAY_DISCOUNT,
    component: UnsubscribeEmailComponent,
    resolve: {
      pageLabels: PageResolver,
    },
  },
  {
    path: appPrefix + appRoutes.CONTACT_US,
    // redirectTo: appPrefix + appRoutes.BIRTHDAY_DISCOUNT,
    component: InquiryWebFormComponent,
    resolve: {
      pageLabels: PageResolver,
    },
  },

  {
    path: appPrefix + appRoutes.CONTACT_US_CONFIRM,
    component: InquiryConfirmComponent,
    resolve: {
      pageLabels: PageResolver,
    },
  },
  {
    path: appPrefix + appRoutes.LOST_FOUND,
    // redirectTo: appPrefix + appRoutes.BIRTHDAY_DISCOUNT,
    component: LostFoundWebFormComponent,
    resolve: {
      pageLabels: PageResolver,
    },
  },
  {
    path: appPrefix + appRoutes.LOST_FOUND_CONFIRM,
    component: LostFoundConfirmComponent,
    resolve: {
      pageLabels: PageResolver,
    },
  },
  {
    path: appRoutes.CART,
    // redirectTo: appPrefix + appRoutes.BIRTHDAY_DISCOUNT,
    component: CartNavigationComponent,
    resolve: {
      pageLabels: PageResolver,
    },
  },
  {
    path: appRoutes.LOGOUT,
    component: LogoutComponent,
  },
  // {
  //   path: appPrefix + appRoutes.SIGN_IN,
  //   component: SignInComponent,
  // },

  {
    path: appPrefix + appRoutes.HOTELS_CHECK_IN + indexPage,
    redirectTo: appPrefix + appRoutes.HOTELS_CHECK_IN,
  },
  {
    path: appPrefix + appRoutes.HOTELS_CHECK_IN,
    component: FlutterLoaderComponent,
    data: {
      assetBase: 'assets/flutter/',
    },
    resolve: {
      urlInfo: UrlInfoResolver,
      pageLabels: PageLabelsResolver,
    },
  },

  {
    path: appPrefix + appRoutes.FAVORITES,
    loadChildren: () =>
      import('../account/my-favorites/my-favorites.module').then(
        (m) => m.MyFavoritesModule,
      ),
    data: {
      slug: appRoutes.FAVORITES,
      featureFlag: FEATURE_FLAGS.GUESTACCOUNT_FAVORITES,
      fallbackRoute: '/dashboard',
    },
    canActivate: [AuthGuard, FeatureFlagGuard],
    resolve: {
      pageLabels: PageLabelsResolver,
    },
  },

  {
    path: appPrefix + appRoutes.PROFILE_SELECTION,
    loadChildren: () =>
      import('../account/my-profile/profile-selection/profile-selection.module').then(
        (m) => m.ProfileSelectionModule,
      ),
    data: {
      slug: appRoutes.PROFILE_SELECTION,
      featureFlag: FEATURE_FLAGS.GUESTACCOUNT_PROFILESELECTION,
      fallbackRoute: '/dashboard',
    },
    canActivate: [AuthGuard, FeatureFlagGuard],
  },

  {
    path: '**',
    component: PageNotFoundComponent,
  },
];

@NgModule({
  imports: [
    RouterModule.forRoot(routes, {
      enableTracing: false,
      scrollPositionRestoration: 'enabled',
    }),
  ],
  exports: [RouterModule],
})
export class RoutingModule {}
